import { useLayoutEffect } from 'react';

import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import type { JiraFilters } from '@atlassian/search-client/query-generator';

import type { GenerateSearchUrlType } from '../../../common/ui/app-context';
import { isJqlQueryDetected } from '../../../common/utils/intent-detection';
import type { QueryParams } from '../../../common/utils/query-params/types';
import { getWorkItemPrefetchUrls } from '../../../common/utils/quick-find/jira-work-item-navigation';
import { useQuickFindActions } from '../../../controllers/store/quick-find';

const PREFETCH_MIN_QUERY_LENGTH = 2;

type PrefetchContext = {
	jiraFilters: JiraFilters;
	generateSearchUrl: GenerateSearchUrlType;
	queryParams: QueryParams;
};

export const dispatchPendingPrefetch = ({
	input,
	query,
	onPrefetch,
	...context
}: PrefetchContext & {
	input: HTMLInputElement;
	query: string;
	onPrefetch: (url: string) => void;
}): void => {
	if (!input.isConnected || input.value !== query) {
		return;
	}
	if (!expVal('quick_find_prefetch', 'isEnabled', false)) {
		return;
	}
	const trimmed = query.trim();
	if (trimmed.length < PREFETCH_MIN_QUERY_LENGTH) {
		return;
	}
	let urls: string[];
	try {
		urls = getWorkItemPrefetchUrls({
			...context,
			query: trimmed,
			isQueryJql: isJqlQueryDetected(trimmed),
		});
	} catch {
		return;
	}
	for (const url of urls) {
		if (url) {
			onPrefetch(url);
		}
	}
};

export const PendingPrefetchCleanup = (): null => {
	const { cancelPendingPrefetch } = useQuickFindActions();
	useLayoutEffect(() => {
		return () => {
			cancelPendingPrefetch();
		};
	}, [cancelPendingPrefetch]);
	return null;
};
